import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Switch } from 'antd';
import emailjs from '@emailjs/browser';
import moment from 'moment';
import SuccessModal from './SuccessModal';
import { Check } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { distributeBenefitsToWeeks } from '../subscriptionUtils';

// --- Helper function with the FINAL, CORRECTED logic ---
const generateSubscriptionBenefits = (plan, duration) => {
  // 1. Define the benefits for a SINGLE month. This is the baseline.
  let monthlyDrinks = 0;
  let monthlyBreakfasts = 0;

  if (plan.name === "Home Brewer") {
    // This plan is always 1 month, so duration is always 1
    monthlyDrinks = 10;
  } else if (plan.name === "Essential") {
    monthlyDrinks = 20;
    monthlyBreakfasts = 4;
  } else if (plan.name === "Daily Ritual") {
    monthlyDrinks = 30;
    monthlyBreakfasts = 12;
  }

  // 2. Create a single, perfect 4-week "template" using the monthly benefits.
  // For Daily Ritual: this will create a block of 4 weeks with ~7-8 drinks and 3 breakfasts each.
  const monthlyBlock = distributeBenefitsToWeeks(monthlyDrinks, monthlyBreakfasts);

  // 3. Create the final array of all weeks by duplicating the template for the chosen duration.
  let allWeeks = [];
  for (let i = 0; i < duration; i++) {
    // Using spread syntax and a deep copy to create new objects, not just references.
    // This prevents all months from sharing the same redeemed count.
    allWeeks.push(...JSON.parse(JSON.stringify(monthlyBlock)));
  }

  // 4. Re-number all the weeks sequentially from 1 up to 4 or 12.
  const finalWeeks = allWeeks.map((week, index) => ({
    ...week,
    week: index + 1,
  }));
  
  const benefits = {
    weeks: finalWeeks, // This will now be a correct 12-week array for 3-month plans
    auto_redeemable: { drinks: { total: 0, redeemed: 0 }, breakfasts: { total: 0, redeemed: 0 } },
    discounts: {},
    gifts: {},
  };

  // Assign gifts and discounts (these are typically per-subscription, not per-month)
  if (plan.name === "Home Brewer") {
    benefits.discounts.store_item_discount = { total: "30% Off" };
  } else if (plan.name === "Essential") {
    benefits.gifts.premium_mug = { total: 1, redeemed: 0 };
    benefits.discounts.menu_discount = { total: "20%" };
    benefits.discounts.shop_discount = { total: "15%" };
  } else if (plan.name === "Daily Ritual") {
    benefits.gifts.premium_mug = { total: 1, redeemed: 0 };
    benefits.gifts.bamboo_saucer = { total: 1, redeemed: 0 };
    benefits.discounts.menu_discount = { total: "20%" };
    benefits.discounts.shop_discount = { total: "15%" };
  }
  return benefits;
};


const SubscriptionFormModal = ({ open, onClose, plan }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState({ open: false, message: '' });
  const [isThreeMonthPlan, setIsThreeMonthPlan] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setIsThreeMonthPlan(false);
    }
  }, [open, plan, form]);

  if (!plan) return null;

  const hasThreeMonthOption = plan.name !== "Home Brewer" && plan.threeMonthPrice;
  const duration = isThreeMonthPlan && hasThreeMonthOption ? 3 : 1;
  const totalPrice = isThreeMonthPlan && hasThreeMonthOption ? plan.threeMonthPrice : plan.monthlyPrice;

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const benefits = generateSubscriptionBenefits(plan, duration);
      const passData = {
        id: `GRMS-${Date.now()}`,
        name: values.full_name,
        plan: plan.name,
        duration: duration,
        price: totalPrice,
        start: null,
        expiry: null,
        status: 'pending',
        benefits: benefits,
        redeemable_days_used: 0,
        carryover_processed_week: 0,
      };
      
      const { error: supabaseError } = await supabase.from('subscriptions').insert([passData]);
      if (supabaseError) throw new Error(`Supabase error: ${supabaseError.message}`);

      // --- Email sending logic ---
      const encodedData = btoa(JSON.stringify({ id: passData.id }));
      const customerUrl = `https://grams.ma/membership/view?data=${encodedData}`;
      const baristaUrl = `https://grams.ma/membership/verify?data=${encodedData}`;
      const serviceID = "service_mgc75k6";
      const customerTemplateID = "template_jswbqyv";
      const baristaTemplateID = "template_5wk5h48";
      const publicKey = "Z6sqHGA2wQR49-3Xx";
      
      const customerTemplateParams = {
        full_name: values.full_name,
        email_address: values.email_address,
        plan_name: plan.name,
        total_price: `${totalPrice} Dh`,
        verification_link: customerUrl,
      };
      const baristaTemplateParams = {
        member_name: values.full_name,
        plan_name: plan.name,
        barista_link: baristaUrl,
      };

      const emailPromises = [
        emailjs.send(serviceID, customerTemplateID, customerTemplateParams, publicKey),
        emailjs.send(serviceID, baristaTemplateID, baristaTemplateParams, publicKey)
      ];
      await Promise.all(emailPromises);
      
      setSuccessModal({ open: true, message: 'Thank you! Please visit us in-store to complete payment and activate your pass.' });
      onClose();

    } catch (error) {
      message.error('Submission failed. Please try again.');
      console.error('Detailed Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onCancel={onClose} title={`Subscribe to: ${plan.name}`} footer={null} destroyOnClose>
        <div className="plan-details-in-modal">
          <div className="plan-header">
            <p className="price">{plan.price}</p>
            {plan.billing && <p className="billing">{plan.billing}</p>}
          </div>
          <ul className="features-list">
            {plan.features.map((feature, index) => (
              <li key={index}><Check size={16} /> <span>{feature}</span></li>
            ))}
          </ul>
        </div>
        <hr className="modal-divider" />

        <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
          
          {hasThreeMonthOption && (
            <div className="month-selector-group">
              <label>Subscription Duration</label>
              <div className="billing-toggle-group">
                <span>Pay Monthly</span>
                <Switch onChange={setIsThreeMonthPlan} checked={isThreeMonthPlan} />
                <span>Pay for 3 Months (Save!)</span>
              </div>
            </div>
          )}

          <div className="total-price-display">
            Total Price: <span>{totalPrice.toFixed(2)} Dh</span>
          </div>
          
          <p style={{ textAlign: "center", margin: "1.5rem 0", color: "#555" }}>
            Please provide your contact details...
          </p>

          <Form.Item name="full_name" label="Full Name" rules={[{ required: true }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="phone_number" label="Phone Number" rules={[{ required: true }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="email_address" label="Email Address" rules={[{ required: true, type: "email" }]}>
            <Input size="large" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              {loading ? "Submitting..." : "Request Subscription"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <SuccessModal open={successModal.open} message={successModal.message} onClose={() => setSuccessModal({ open: false, message: "" })}/>
    </>
  );
};

export default SubscriptionFormModal;